import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutGrid,
  Bot,
  FileSignature,
  UserSearch,
  Route as RouteIcon,
  Receipt,
  RefreshCcw,
  Plug,
  BadgeCheck,
  Terminal,
  Percent,
  Fingerprint,
  Building2,
  LogOut,
  ArrowRightLeft,
  BookOpen,
  Compass,
} from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";
import DemoTour, { startTour } from "@/components/DemoTour";
import { Wordmark, useBrand } from "@/context/BrandContext";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutGrid, testId: "nav-overview" },
  { to: "/agent", label: "Agent Console", icon: Bot, testId: "nav-agent" },
  { to: "/designer", label: "Authorization Designer", icon: FileSignature, testId: "nav-designer" },
  { to: "/buyer", label: "Buyer Resolver", icon: UserSearch, testId: "nav-buyer" },
  { to: "/routing", label: "Order Routing", icon: RouteIcon, testId: "nav-routing" },
  { to: "/rate-cards", label: "Rate Cards", icon: Percent, testId: "nav-rate-cards" },
  { to: "/offers", label: "Private Offers", icon: Receipt, testId: "nav-offers" },
  { to: "/renewals", label: "Renewals", icon: RefreshCcw, testId: "nav-renewals" },
  { to: "/handoff", label: "Deal hand-offs", icon: ArrowRightLeft, testId: "nav-handoff" },
  { to: "/playbooks", label: "Incentive Playbooks", icon: BookOpen, testId: "nav-playbooks" },
  { to: "/audit", label: "Trace Audit", icon: Fingerprint, testId: "nav-audit" },
  { to: "/integrations", label: "Integrations", icon: Plug, testId: "nav-integrations" },
  { to: "/cp-registration", label: "CP Registration", icon: BadgeCheck, testId: "nav-cp" },
  { to: "/workspace", label: "Workspace", icon: Building2, testId: "nav-workspace" },
];

export default function Layout() {
  const { user, workspace, logout, demo } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "?").split(/[\s@]/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("");
  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  return (
    <div className="h-screen overflow-hidden flex bg-zinc-50 text-zinc-900">
      <aside
        data-testid="app-sidebar"
        className="w-[272px] border-r border-zinc-200 flex flex-col shrink-0 bg-white"
      >
        <Link
          to="/"
          data-testid="app-logo"
          className="h-16 px-6 border-b border-zinc-200 flex items-center gap-2.5"
        >
          <Wordmark />
        </Link>

        <nav className="flex-1 py-6 px-3 space-y-1">
          <div className="tx-overline px-3 pb-3">Workspace</div>
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 h-10 rounded-sm text-[13px] transition-colors ${
                  isActive
                    ? "bg-zinc-100 text-zinc-900 font-medium"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-4 text-[11px] text-zinc-500 font-mono">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full tx-pulse-dot" />
            <span>agent runtime online</span>
          </div>
          <div>claude-sonnet-4.5 · MCP ready</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 h-full flex flex-col">
        <header className="h-16 border-b border-zinc-200 px-8 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-3 text-[13px] text-zinc-600">
            <Terminal className="w-4 h-4" />
            <span className="font-mono" data-testid="header-workspace-slug">isv://{workspace?.slug || "…"}</span>
            <span className="text-zinc-300">·</span>
            <span data-testid="header-workspace-name">{workspace?.name || brand.app_name}</span>
          </div>
          <div className="flex items-center gap-3 text-[12px] text-zinc-600">
            <span className="hidden sm:inline font-mono text-[11px]" data-testid="header-user-email">{user?.email}</span>
            <span className="hidden sm:inline tx-overline">{user?.role}</span>
            {demo && (
              <span data-testid="demo-badge" className="hidden sm:inline font-mono text-[10px] text-orange-700 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded-sm">
                DEMO · login disabled
              </span>
            )}
            <button
              onClick={startTour}
              data-testid="start-tour-button"
              className="h-8 px-3 border border-zinc-200 rounded-sm flex items-center gap-1.5 text-[11px] font-mono hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <Compass className="w-3.5 h-3.5 text-orange-600" /> Tour
            </button>
            <NotificationsBell />
            <div className="w-8 h-8 border border-zinc-200 rounded-sm flex items-center justify-center bg-zinc-50 font-mono text-[11px] text-zinc-700">
              {initials}
            </div>
            {!demo && <button
              onClick={signOut}
              data-testid="logout-button"
              title="Sign out"
              className="w-8 h-8 border border-zinc-200 rounded-sm flex items-center justify-center hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>}
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </div>
      </main>
      <DemoTour enabled={demo} />
    </div>
  );
}
